import { type FC, useEffect } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { useHistory } from "react-router";

import { Text, toastError } from "@alpacahq/ui/old";

import {
  getErrorMessage,
  useGetOwnerDetails,
  useMutationRequest,
} from "~/v2/api/hooks";
import { patchAccount, patchDetails } from "~/v2/api/rest/account";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { usePageTimer } from "../../../entityOnboarding/usePageTimer";
import { Layout } from "../../layout";
import { ProgressionButton } from "../../ProgressionButton";
import { FinancialProfileStep } from "../../steps";
import { getUTMsFromSearch, logAmplitudeEvent, storage } from "../../util";
import { ConvertCashInput } from "./ConvertCashInput";
import { FinancialInputs } from "./FinancialInputs";
import { MaritalStatusInputs } from "./MartialStatusInputs";
import { type FinancialProfileForm, zFinancialProfileForm } from "./schema";

export const FinancialPage: FC = () => {
  const history = useHistory();

  const { currentAccount } = useAccountContext({ liveOnly: true });
  const { details } = useGetOwnerDetails();

  const pageTimer = usePageTimer("options_financial_profile_page");

  const form = useForm<FinancialProfileForm>({
    resolver: zodResolver(zFinancialProfileForm),
    mode: "onChange",
  });

  const mutation = useMutationRequest(
    ({ liquidity_needs, ...payload }: FinancialProfileForm) =>
      Promise.all([
        patchDetails(currentAccount?.id ?? "", payload),
        patchAccount(currentAccount?.id ?? "", { liquidity_needs }),
      ]),
    {
      onSuccess: () => {
        const duration = pageTimer.stop();
        logAmplitudeEvent.pageCompleted(duration);

        history.push(Path.ROUTE_OPTIONS_OPT_IN_INVESTMENT_EXPERIENCE);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Error: ${errorMessage}`);
      },
    },
  );

  const onCancelClicked = () => {
    // Clean up storage and timers, opt in flow cancelled
    storage.removeTier();
    storage.endTimer();
    pageTimer.stop();
    logAmplitudeEvent.backClicked();
    history.push(Path.ROUTE_DASHBOARD);
  };

  const onSubmitInvalid: SubmitErrorHandler<FinancialProfileForm> = (
    _errors,
  ) => {
    toastError("Error: Invalid Form. Please fill out all required fields");
  };

  const onSubmit: SubmitHandler<FinancialProfileForm> = (values) => {
    logAmplitudeEvent.nextClicked();
    mutation.mutate(values);
  };

  useEffect(() => {
    if (details) {
      form.reset({
        annual_income_max: Number(details.annual_income_max),
        annual_income_min: Number(details.annual_income_min),
        liquid_net_worth_max: Number(details.liquid_net_worth_max),
        liquid_net_worth_min: Number(details.liquid_net_worth_min),
        total_net_worth_max: Number(details.total_net_worth_max),
        total_net_worth_min: Number(details.total_net_worth_min),
        liquidity_needs: currentAccount?.liquidity_needs,
        marital_status: details.marital_status,
        number_of_dependents: details.number_of_dependents,
      } as FinancialProfileForm);
    }
  }, [details, form.reset, currentAccount?.liquidity_needs]);

  useEffect(() => {
    // Track total time spend in options opt-in flow in local storage
    storage.startTimer();

    // Capture UTMs - Do not override if already set
    if (!storage.getUTMs()) {
      const utms = getUTMsFromSearch(window.location.search);

      if (utms) {
        storage.setUTMs(utms);
      }
    }
  }, []);

  return (
    <Layout currentStep={FinancialProfileStep}>
      <h2 className="pb-6 font-bold text-xl">Financial Profile</h2>
      <form onSubmit={form.handleSubmit(onSubmit, onSubmitInvalid)}>
        <FinancialInputs form={form} />
        <ConvertCashInput form={form} />
        <Text className="mt-6 pb-6 font-bold text-xl">Personal Profile</Text>
        <MaritalStatusInputs form={form} />
        <div className="flex max-w-lg items-center justify-between pt-16">
          <ProgressionButton
            variant="secondary"
            onClick={onCancelClicked}
            type="button"
          >
            Cancel
          </ProgressionButton>
          <ProgressionButton type="submit">Next</ProgressionButton>
        </div>
      </form>
    </Layout>
  );
};

export default FinancialPage;
